import { useEffect } from "react";
import {
  calculateWetBulbTemperature,
  calculateDewPoint,
  findHumidityRatio,
} from "../calculationFunctions";
import { etyleneGlycol, propyleneGlycol } from "../glycolConstants";

/**
 * Custom hook to handle automatic form field calculations
 * Updates related fields when dependencies change
 */
export const useFormFieldCalculations = (watch, setValue) => {
  // Watch values needed for calculations
  const summerOutdoorTemperature = watch("summerOutdoorTemperature");
  const summerOutdoorHumidity = watch("summerOutdoorHumidity");
  const winterOutdoorTemperature = watch("winterOutdoorTemperature");
  const winterOutdoorHumidity = watch("winterOutdoorHumidity");
  const elevation = watch("elevation");
  const heaterType = watch("heaterType");
  const coolerType = watch("coolerType");

  // Calculate summer conditions (wet bulb and dew point)
  useEffect(() => {
    if (
      summerOutdoorTemperature !== undefined &&
      summerOutdoorTemperature !== "" &&
      summerOutdoorHumidity !== undefined &&
      summerOutdoorHumidity !== ""
    ) {
      const wetBulb = calculateWetBulbTemperature(
        summerOutdoorTemperature,
        summerOutdoorHumidity
      );
      const dewPoint = calculateDewPoint(
        summerOutdoorTemperature,
        summerOutdoorHumidity
      );

      setValue("summerWetBulbTemperature", wetBulb, { shouldValidate: false });
      setValue("summerDewPointTemperature", dewPoint, {
        shouldValidate: false,
      });
    }
  }, [summerOutdoorTemperature, summerOutdoorHumidity, setValue]);

  // Calculate winter humidity ratio
  useEffect(() => {
    if (
      winterOutdoorTemperature !== undefined &&
      winterOutdoorTemperature !== "" &&
      winterOutdoorHumidity !== undefined &&
      winterOutdoorHumidity !== "" &&
      elevation !== undefined &&
      elevation !== ""
    ) {
      const humidityRatio = findHumidityRatio(
        winterOutdoorTemperature,
        winterOutdoorHumidity,
        elevation
      );
      setValue("winterOutdoorHumidityRatio", humidityRatio, {
        shouldValidate: false,
      });
    }
  }, [winterOutdoorTemperature, winterOutdoorHumidity, elevation, setValue]);

  // Update glycol concentration based on heater type and winter temperature
  useEffect(() => {
    if (
      winterOutdoorTemperature !== undefined &&
      winterOutdoorTemperature !== "" &&
      heaterType !== undefined &&
      heaterType !== ""
    ) {
      let concentration = 0;

      if (heaterType === "ethylene") {
        // Find closest key less than winter temperature
        const keys = Object.keys(etyleneGlycol)
          .map(Number)
          .filter((key) => etyleneGlycol[key] < winterOutdoorTemperature);
        if (keys.length > 0) {
          const closestKey = Math.min(...keys);
          concentration = closestKey;
        }
      } else if (heaterType === "propylene") {
        const keys = Object.keys(propyleneGlycol)
          .map(Number)
          .filter((key) => propyleneGlycol[key] < winterOutdoorTemperature);
        if (keys.length > 0) {
          const closestKey = Math.min(...keys);
          concentration = closestKey;
        }
      }

      setValue("heaterConcentration", concentration, { shouldValidate: false });
    }
  }, [winterOutdoorTemperature, heaterType, setValue]);

  // Update glycol concentration based on cooler type and winter temperature
  useEffect(() => {
    if (
      winterOutdoorTemperature !== undefined &&
      winterOutdoorTemperature !== "" &&
      coolerType !== undefined &&
      coolerType !== ""
    ) {
      let concentration = 0;

      if (coolerType === "ethylene") {
        const keys = Object.keys(etyleneGlycol)
          .map(Number)
          .filter((key) => etyleneGlycol[key] < winterOutdoorTemperature);
        if (keys.length > 0) {
          const closestKey = Math.max(...keys);
          concentration = closestKey;
        }
      } else if (coolerType === "propylene") {
        const keys = Object.keys(propyleneGlycol)
          .map(Number)
          .filter((key) => propyleneGlycol[key] < winterOutdoorTemperature);
        if (keys.length > 0) {
          const closestKey = Math.max(...keys);
          concentration = closestKey;
        }
      }

      setValue("coolerConcentration", concentration, { shouldValidate: false });
    }
  }, [winterOutdoorTemperature, coolerType, setValue]);
};
